import * as React from "react"
import PropTypes from "prop-types"
import { useParams } from "react-router-dom"

// components
import Field from "containers/requests/components/Field/Field"
import { Select } from "components-design-system"
import PrimeField from "components/PrimeField/PrimeField"
import SecondaryIdField from "../SecondaryIdField/SecondaryIdField"

// styles
import "./RequestAllFields.scss"

// apis
import { useGetRequestCategories } from "api/hooks/admin/useRequestCategoriesApi"
import { useGetRequest } from "api/hooks/requests/useRequestsApi"
import { useGetRequestAdditionaFields } from "api/hooks/admin/workflowApis/useRequestTypeApis"
import { useGetStudiesList } from "api/hooks/enum"

// utils
import useGlobalFilterStudies from "utilities/useGlobalFilterStudies"

export const CORE_FIELDS = {
  request_id: {
    type: "text",
    label: "Request ID",
    readOnly: true,
    width: 186,
    containerClassName: "md-field",
    baseClassName: "sm-field",
    base: true,
  },
  request_type_name: {
    type: "text",
    label: "Request type",
    readOnly: true,
    width: 186,
    containerClassName: "md-field",
    baseClassName: "sm-field",
    base: true,
  },
  request_category_id: {
    type: "dropdown",
    label: "Request category",
    width: 186,
    containerClassName: "md-field",
    items: [],
  },
  "request_data.requestor": {
    type: "text",
    label: "Requestor",
    width: 186,
    containerClassName: "md-field",
    baseClassName: "sm-field",
    base: true,
  },
  "request_data.date_requested": {
    type: "date",
    label: "Requested date",
    width: 130,
    containerClassName: "sm-field",
    baseClassName: "sm-field",
    base: true,
  },
  created_at: {
    type: "date",
    label: "Created date",
    readOnly: true,
    width: 130,
    containerClassName: "sm-field",
    baseClassName: "sm-field",
    base: true,
  },
  due_date: {
    type: "date",
    label: "Due date",
    width: 130,
    containerClassName: "sm-field",
    baseClassName: "",
  },
  "request_data.description": {
    type: "textarea",
    label: "Description",
    containerClassName: "full-field",
  },
  "request_data.secondary_ids": {
    label: "Secondary ID's",
    width: "calc(80vw - 96px)",
  },
  "request_data.rationale": {
    type: "textarea",
    label: "Rationale",
    containerClassName: "full-field",
  },
  "request_data.organization": {
    type: "text",
    label: "Organization",
    containerClassName: "md-field",
  },
  "request_data.department": {
    type: "text",
    label: "Department",
    containerClassName: "md-field",
  },
  "request_data.point_of_contact": {
    type: "text",
    label: "Point of contact",
    containerClassName: "xl-field",
  },
  "request_data.study_db_ids": {
    label: "Studies",
    base: true,
  },
  "request_data.study_data.product_id": {
    type: "multiselect",
    label: "Product ID(s)",
    mode: "tags",
    containerClassName: "md-field",
  },
  "request_data.study_data.product_name": {
    type: "multiselect",
    label: "Product name(s)",
    mode: "tags",
    containerClassName: "md-field",
  },
  "request_data.study_data.therapeutic_area": {
    type: "multiselect",
    label: "Therapeutic area(s)",
    mode: "tags",
    containerClassName: "lg-field",
    items: [],
  },
  "request_data.study_data.indication": {
    type: "multiselect",
    label: "Indication(s)",
    mode: "tags",
    containerClassName: "lg-field",
    items: [],
  },
  "request_data.study_data.program_name": {
    type: "multiselect",
    label: "Program name(s)",
    mode: "tags",
    containerClassName: "md-field",
  },
}

const syncKeySet = new Set([
  "request_data.study_data.product_id",
  "request_data.study_data.product_name",
  "request_data.study_data.therapeutic_area",
  "request_data.study_data.indication",
  "request_data.study_data.program_name",
])

const RequestAllFields = ({
  requestFormData,
  onChangeRequest = () => {},
  selectedStudies,
  setSelectedStudies,
  isReviewerView = false,
  readOnly,
}) => {
  const { requestId } = useParams()
  const { isGlobalFilterStudy, studyOptions, studiesSelectItemRender } = useGlobalFilterStudies()

  // server state
  const { data: requestCategories = [] } = useGetRequestCategories()
  const { data: request } = useGetRequest(requestId)
  const { data: schema = [], refetch: refetchAdditionalFields } = useGetRequestAdditionaFields(request?.request_type_id)
  const { data: studiesList } = useGetStudiesList()

  // local state
  const [addtlDetailsOpen, setAddtlDetailsOpen] = React.useState(false)

  React.useEffect(() => {
    refetchAdditionalFields()
  }, [])

  const getValue = (key) => {
    const keys = key.split(".")
    const fieldKey = keys.pop()
    const obj = keys.reduce((nestedObj, nestedKey) => nestedObj?.[nestedKey], requestFormData)
    return obj?.[fieldKey] || undefined
  }

  const handleChange = (key, val) => {
    let value = val
    if (syncKeySet.has(key)) {
      value = value.join(", ")
    }
    onChangeRequest(key, value)
  }

  const renderStudiesField = (field) => {
    // reviewer view read-only value
    let defaultValue = Array.isArray(requestFormData?.corestudies)
      ? requestFormData.corestudies.map((study) => ({ value: study.id, label: study.study_id }))
      : []
    // TODO: review if need to look at "request_data.study_db_ids"
    if (requestFormData.request_data.study_db_ids)
      defaultValue = requestFormData.request_data.study_db_ids.map((study) => ({
        value: study.id,
        label: study.study_id,
      }))

    return (
      <Field
        label={field.label}
        className="xl-field"
        key="req-studies-field"
      >
        <Select
          options={studyOptions}
          value={isReviewerView ? defaultValue : selectedStudies}
          tagRender={studiesSelectItemRender}
          onChange={(val) => {
            let selected = [...val]
            // when user clears all studies, keep the ones that are not in the global filter
            if (Array.isArray(val) && val.length === 0) {
              selected = defaultValue
                .filter(
                  (studyObj) =>
                    !isGlobalFilterStudy(studiesList.find((listObj) => listObj.id === studyObj.value)?.study_id)
                )
                ?.map((studyObj) => studyObj.value)
            }
            setSelectedStudies(selected)
          }}
          mode="multiple"
          readOnly={readOnly}
          disabled={isReviewerView}
          allowClear
          className="studies-select"
        />
      </Field>
    )
  }

  const renderSecondaryIdField = (field, key) => {
    return (
      <div
        style={{ width: field.width }}
        key="req-secondary-ids-field"
      >
        <Field
          label={field.label}
          className="studies-field"
        >
          <SecondaryIdField
            field={field}
            fieldKey={key}
            onChange={(key, val) => onChangeRequest(key, val)}
            value={requestFormData.request_data.secondary_ids || []}
            readOnly={isReviewerView}
          />
        </Field>
      </div>
    )
  }

  const renderAdditionalFields = () => {
    return schema.map((fieldObj) => {
      let field = fieldObj.field_data

      let classNames = ["request-addtl-field"]

      if (field.type === "textarea") {
        classNames.push("full-field")
      } else if (field.type === "boolean" || field.type === "boolean") {
        classNames.push("sm-field")
      } else {
        classNames.push("lg-field")
      }
      return (
        <Field
          key={fieldObj.id}
          label={field.label}
          className={classNames.join(" ")}
        >
          <PrimeField
            schema={{
              ...field,
              placeholder: isReviewerView ? undefined : field.placeholder,
              label: null,
            }}
            readOnly={isReviewerView}
            value={getValue(`request_data.custom_data.${field.key}`)}
            onChange={(val) => onChangeRequest(`request_data.custom_data.${field.key}`, val)}
          />
        </Field>
      )
    })
  }

  const renderFields = () => {
    return Object.keys(CORE_FIELDS).map((key) => {
      const field = CORE_FIELDS[key]
      let items = field.items
      let value = getValue(key)

      if (key === "request_data.study_db_ids") return renderStudiesField(field)
      if (key === "request_data.secondary_ids") return renderSecondaryIdField(field, key)
      if (key === "request_category_id") {
        items = requestCategories.map((cat) => ({ value: cat.id, label: cat.name }))
        value = items.find((item) => item.value === value)?.label // works for both read-only and non-read-only states
      }
      if (syncKeySet.has(key)) {
        if (!value || value.length === 0) {
          items = []
          value = []
        } else {
          items = value.split(", ")
          value = value.split(", ")
        }
      }

      let classNames = ["request-addtl-field"]
      if (field.containerClassName) classNames.push(field.containerClassName)
      return (
        <Field
          key={key}
          label={field.label}
          className={classNames.join(" ")}
        >
          <PrimeField
            schema={{
              type: field.type,
              placeholder: field.placeholder,
              items,
              mode: field.mode,
            }}
            onChange={(val) => handleChange(key, val)}
            readOnly={field.readOnly || isReviewerView}
            value={value}
            className={field.className}
          />
        </Field>
      )
    })
  }

  return (
    <div className="edit-request-info">
      {renderFields()}
      {schema?.length > 0 && (
        <div className="addtl-details">
          <div
            onClick={() => setAddtlDetailsOpen((state) => !state)}
            className="addtl-details-title"
          >
            Additional details <i className={`far fa-angle-${addtlDetailsOpen ? "down" : "up"}`} />
          </div>
          {addtlDetailsOpen && <div className="addtl-content">{renderAdditionalFields()}</div>}
        </div>
      )}
    </div>
  )
}

RequestAllFields.propTypes = {
  requestFormData: PropTypes.shape({
    due_date: PropTypes.string,
    requestor: PropTypes.string,
    ask_num: PropTypes.string,
    completed: PropTypes.string,
    description: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
}

export default RequestAllFields
